import React, { useState } from "react";
import PropTypes, { oneOfType } from "prop-types";
import TextField from "@material-ui/core/Input";
import IconButton from "@material-ui/core/IconButton";
import Creatable from "react-select/lib/Creatable";
import ReactSelect from "react-select";
import { FormControl, InputLabel, withStyles } from "@material-ui/core";
import SpinnerSVG from "static/spinner.svg";
import { getPasteableStrings } from "components/select/common";
import styles from "components/select/react-select-styles";
import { MultiValueRemove, MultiValueContainer } from "components/select/Chip";
import customStyles from "components/select/styles";

const filter = (candidate, input) => {
  if (!input) return true;
  if (!(candidate.label && candidate.value)) return false;

  return (
    candidate.label.toUpperCase().includes(input.toUpperCase()) ||
    // checking value as well- could take this out.
    // conerts to string in case it was numeric..
    `${candidate.value}`.toUpperCase().includes(input.toUpperCase())
  );
};

const selectAll = { label: "Select All", value: "select_all" };

const Select = props => {
  const {
    value,
    isMulti,
    label,
    placeholder,
    onChange,
    options,
    useCreatable,
    isClearable,
    isLoading,
    disabled,
    extraIcon,
    onExtraIconClick,
    isGrouped
  } = props;

  // since TextField gets the value of the select component, this is the only way that I could find
  // to keep track of the typed text

  // the text in the inner input component of the TextField
  const [inputValue, setInputValue] = useState("");

  const getOptionByValue = val => {
    if (isGrouped) {
      // combine grouped options
      const totalOptions = [];
      options.forEach(groupsOrObj => {
        if (groupsOrObj.options) totalOptions.push(...groupsOrObj.options);
        else totalOptions.push(groupsOrObj);
      });
      return totalOptions.find(e => e.value === val);
    }
    return options.find(e => e.value === val);
    // get the label for the current value
  };

  // value can be a string or an array of strings

  // make it look more like MUI
  // The built-in Option component is fast so I kept it instead of a MUI component

  // value can be undefined/null. Eg sample picker before a sample type is selected
  const valueIsSet = isMulti ? value && value.length > 0 : Boolean(value);

  const valueIsArray = Array.isArray(value);

  // object or array of labels/value objects
  const valueArray = valueIsArray ? value : [value].filter(e => e);

  // options can be an array of {label:bla value:bla} objects, or an array of
  // plain strings
  const optionsHaveLabels =
    options && options.length > 0 && typeof options[0] !== "string";

  // its ok if this gets sent to a non-multi select as an array with a single object
  const labelAndValue = valueArray
    .map(v =>
      optionsHaveLabels ? getOptionByValue(v) : { value: v, label: v }
    )
    .filter(e => e);

  const optionsWithLabels = optionsHaveLabels
    ? options
    : options.map(str => ({ value: str, label: str }));

  // this is a big slow down
  const optionsWithLabelsSorted = optionsWithLabels.sort((a, b) =>
    a.label.localeCompare(b.label, "en", { sensitivity: "base" })
  );

  // current filtered drop down options, after filter is applied
  // there is probably a better way to access this internal array
  const optionsFilteredToInput = optionsWithLabelsSorted.filter(option =>
    filter(option, inputValue)
  );

  const allOptionsAreSelected =
    isMulti && valueIsSet && optionsFilteredToInput.length === value.length;

  const useSelectAll =
    isMulti && optionsFilteredToInput.length > 1 && !allOptionsAreSelected;

  let components;

  if (isMulti) {
    // add custom chip component for multi select
    components = { ...components, MultiValueRemove, MultiValueContainer };
  }

  const optionsCleaned = useSelectAll
    ? optionsWithLabelsSorted.concat(selectAll)
    : optionsWithLabelsSorted;

  const inputProps = {
    isDisabled: disabled,
    onInputChange: e => {
      setInputValue(e);
    },
    styles: customStyles,
    isMulti,
    isClearable,
    border: "none",
    inputValue,
    // if value is displayed before the options have loaded, it will flash the value (instead of the label)
    value: useCreatable
      ? labelAndValue
      : optionsCleaned.length && labelAndValue,
    components,
    options: optionsCleaned
  };

  if (useSelectAll) inputProps.filterOption = filter;

  return (
    <FormControl fullWidth style={{ marginBottom: "16px" }}>
      <InputLabel shrink>{label}</InputLabel>
      <TextField
        multiline
        fullWidth
        disabled={disabled}
        style={extraIcon ? { paddingRight: "50px" } : {}}
        onChange={val => {
          // val is similar to value, it can be an array or a string
          if (isMulti) {
            const selectAllWasSelected = val.some(
              chip => chip.value === selectAll.value
            );
            if (useSelectAll && selectAllWasSelected) {
              setInputValue("");

              if (inputValue)
                return onChange(optionsFilteredToInput.map(e => e.value));
              // no filter, show add all options
              return onChange(optionsWithLabelsSorted.map(e => e.value));
            }
            return onChange(val.map(e => e.value));
          }

          return onChange(val);
        }}
        placeholder={
          <span>
            {placeholder} {isLoading && <SpinnerSVG width="20" height="20" />}
          </span>
        }
        onPaste={e => {
          if (!isMulti) return;
          onChange([...(value || []), ...getPasteableStrings(e)]);

          // without blurring, the text gets converted to chips but also stays in input
          e.target.blur();
        }}
        inputComponent={useCreatable ? Creatable : ReactSelect}
        inputProps={inputProps}
      />{" "}
      {extraIcon && (
        <IconButton
          style={{
            position: "absolute",
            right: "1px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1
          }}
          onClick={onExtraIconClick}
        >
          {extraIcon}
        </IconButton>
      )}
    </FormControl>
  );
};

const valueLabelShape = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.string
});

Select.propTypes = {
  // array of options can be array of strings or array of {label,value} objects
  // if you need the label to be different than the value
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(valueLabelShape)
  ]).isRequired,

  // when a new chip is selected
  onChange: PropTypes.func.isRequired,
  // current chips
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(valueLabelShape),
    PropTypes.arrayOf(PropTypes.string),
    valueLabelShape,
    PropTypes.string
  ]),
  // the default text when there's no chips
  placeholder: oneOfType([PropTypes.string, PropTypes.node]),
  // name of the field, text that appears above field when there are chips
  label: PropTypes.string,
  isMulti: PropTypes.bool,
  isGrouped: PropTypes.bool,
  useCreatable: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,

  extraIcon: PropTypes.node,
  onExtraIconClick: PropTypes.func
};
Select.defaultProps = {
  placeholder: "",
  label: "",
  isMulti: false,
  isGrouped: false,
  useCreatable: false,
  isLoading: false,
  isClearable: false,
  disabled: false,
  value: [],

  extraIcon: null,
  onExtraIconClick: () => {}
};

export default withStyles(styles)(Select);
